import { useEffect, useRef, useState } from "react";
import type { MouseEvent as ReactMouseEvent, ReactElement } from "react";
import {
  UNSAFE_LocationContext as LocationContext,
  matchPath,
  useLocation,
  useNavigate,
  useNavigationType,
  useOutlet,
} from "react-router-dom";
import { X } from "lucide-react";
import { Header } from "./Header";
import Sidebar from "./SidebarNew";

// تبويبات داخل النظام: كل قسم يُفتح في تبويب داخل شاشة الأدمن/الموظف (وليس في تبويب المتصفح)
// الصفحات المفتوحة تبقى محفوظة بحالتها (مدخلات، فلاتر) عند التنقل بين التبويبات.

type Tab = { id: string; path: string; search: string; title: string };
type NavState = { newTab?: boolean; title?: string; switchTo?: string } | null;

const MAX_TABS = 12;

const TITLES: Array<[string, string]> = [
  ["/dashboard", "لوحة التحكم"],
  ["/shipments/scan", "مسح الشحنات"],
  ["/shipments/upload", "رفع شحنات"],
  ["/shipments/create", "شحنة جديدة"],
  ["/shipments/new", "شحنة جديدة"],
  ["/shipments/pending", "شحنات معلقة"],
  ["/shipments/awaiting-approval", "بانتظار الاعتماد"],
  ["/shipments/:id/edit", "تعديل شحنة"],
  ["/shipments/:id/print", "طباعة شحنة"],
  ["/shipments/:id", "تفاصيل شحنة"],
  ["/shipments", "الشحنات"],
  ["/branch-transfers", "التحويل بين الفروع"],
  ["/accounting/statement", "كشف حساب العميل"],
  ["/accounting/cod", "محاسبة COD"],
  ["/pickups/new", "طلب بيك أب جديد"],
  ["/pickups", "طلبات البيك أب"],
  ["/customers/new", "عميل جديد"],
  ["/customers", "العملاء"],
  ["/delegates/new", "مندوب جديد"],
  ["/delegates", "المناديب"],
  ["/users/new", "مستخدم جديد"],
  ["/users/edit/:id", "تعديل مستخدم"],
  ["/users", "المستخدمون"],
  ["/roles/new", "دور جديد"],
  ["/roles/:id", "تعديل دور"],
  ["/roles", "الأدوار والصلاحيات"],
  ["/branches/new", "فرع جديد"],
  ["/branches", "الفروع"],
  ["/drs/new", "DR جديد"],
  ["/drs/:id", "تفاصيل DR"],
  ["/drs", "إيصالات التوصيل"],
  ["/invoices/new", "فاتورة جديدة"],
  ["/invoices", "الفواتير"],
  ["/complaints/new", "شكوى جديدة"],
  ["/complaints", "الشكاوى"],
  ["/returns", "المرتجعات"],
  ["/audit-logs", "سجلات التدقيق"],
  ["/notifications", "الإشعارات"],
  ["/whatsapp-bot", "بوت واتساب"],
  ["/whatsapp", "واتساب"],
  ["/email", "البريد الإلكتروني"],
  ["/reports", "التقارير"],
  ["/financial-reports", "التقارير المالية"],
  ["/coupons", "الكوبونات"],
  ["/products", "المنتجات"],
  ["/store", "المتجر"],
  ["/settings", "الإعدادات"],
  ["/profile", "الملف الشخصي"],
  ["/gps-tracking", "التتبع المباشر"],
  ["/cities", "المدن"],
  ["/zones", "المناطق"],
  ["/employees", "الموظفون"],
  ["/vans", "السيارات"],
  ["/warehouses", "المخازن"],
  ["/van-stocks", "مخزون السيارات"],
  ["/accounts-chart", "شجرة الحسابات"],
  ["/accounting-entries", "القيود اليومية"],
  ["/taxes", "الضرائب"],
  ["/banks", "البنوك"],
  ["/bank-accounts", "الحسابات البنكية"],
  ["/receipts", "سندات القبض"],
  ["/payment-statuses", "حالات الدفع"],
  ["/price-categories", "فئات الأسعار"],
  ["/payments", "بوابة الدفع"],
  ["/ratings", "التقييمات"],
  ["/loyalty", "برنامج الولاء"],
  ["/integrations", "الربط البرمجي"],
  ["/templates", "القوالب"],
  ["/contact-messages", "رسائل اتصل بنا"],
  ["/ceo-dashboard", "لوحة المدير التنفيذي"],
  ["/delegate-commissions", "عمولات المناديب"],
];

function titleFor(pathname: string): string | null {
  for (const [pattern, title] of TITLES) {
    if (matchPath({ path: pattern, end: true }, pathname)) return title;
  }
  return null;
}

const STORE_KEY = "te_open_tabs_v1";

function loadStored(): { tabs: Tab[]; activeId: string } {
  try {
    const raw = sessionStorage.getItem(STORE_KEY);
    if (raw) {
      const j = JSON.parse(raw);
      if (Array.isArray(j.tabs) && j.tabs.every((t: Tab) => t && t.id && typeof t.path === "string")) {
        return { tabs: j.tabs.slice(0, MAX_TABS), activeId: String(j.activeId || "") };
      }
    }
  } catch {
    /* التخزين غير متاح: نكمل بدون حفظ */
  }
  return { tabs: [], activeId: "" };
}

let tabCounter = 0;
const newId = () => `tab-${Date.now()}-${++tabCounter}`;

export default function MainLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const navType = useNavigationType();
  const outlet = useOutlet();

  const stored = useRef(loadStored()).current;
  const [tabs, setTabs] = useState<Tab[]>(stored.tabs);
  const [activeId, setActiveId] = useState<string>(stored.activeId);
  const tabsRef = useRef<Tab[]>(stored.tabs);
  const activeRef = useRef<string>(stored.activeId);
  const firstRun = useRef(true);

  // حفظ التبويبات المفتوحة ليستعيدها المتصفح بعد تحديث الصفحة
  useEffect(() => {
    try {
      sessionStorage.setItem(STORE_KEY, JSON.stringify({ tabs, activeId }));
    } catch {
      /* ignore */
    }
  }, [tabs, activeId]);
  const elements = useRef<Record<string, ReactElement | null>>({});

  // مزامنة التبويبات مع التنقل
  useEffect(() => {
    const st = location.state as NavState;
    const cur = tabsRef.current;
    let next = cur;
    let nextActive = activeRef.current;

    const isFirst = firstRun.current;
    firstRun.current = false;

    if (isFirst && cur.length > 0) {
      // بعد تحديث الصفحة: نفعّل التبويب المطابق للعنوان الحالي أو نضيف تبويباً له
      const same = cur.find((t) => t.path === location.pathname);
      if (same) {
        nextActive = same.id;
        next = cur.map((t) => (t.id === same.id ? { ...t, search: location.search } : t));
      } else {
        const t: Tab = {
          id: newId(),
          path: location.pathname,
          search: location.search,
          title: titleFor(location.pathname) || "الصفحة",
        };
        next = [...cur, t].slice(-MAX_TABS);
        nextActive = t.id;
      }
    } else if (st?.switchTo && cur.some((t) => t.id === st.switchTo)) {
      nextActive = st.switchTo;
    } else if (cur.length === 0) {
      const t: Tab = {
        id: newId(),
        path: location.pathname,
        search: location.search,
        title: titleFor(location.pathname) || st?.title || "الصفحة",
      };
      next = [t];
      nextActive = t.id;
    } else if (st?.newTab) {
      const same = cur.find((t) => t.path === location.pathname);
      if (same) {
        nextActive = same.id;
        next = cur.map((t) => (t.id === same.id ? { ...t, search: location.search } : t));
      } else {
        const t: Tab = {
          id: newId(),
          path: location.pathname,
          search: location.search,
          title: titleFor(location.pathname) || st.title || "الصفحة",
        };
        next = [...cur, t];
        nextActive = t.id;
        if (next.length > MAX_TABS) {
          const victim = next.find((x) => x.id !== nextActive);
          if (victim) {
            next = next.filter((x) => x.id !== victim.id);
            delete elements.current[victim.id];
          }
        }
      }
    } else {
      // تنقل داخل الصفحة نفسها (زر داخلي، رجوع...): يحدّث التبويب الحالي
      next = cur.map((t) =>
        t.id === nextActive
          ? { ...t, path: location.pathname, search: location.search, title: titleFor(location.pathname) || t.title }
          : t
      );
    }

    tabsRef.current = next;
    activeRef.current = nextActive;
    setTabs(next);
    setActiveId(nextActive);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const activeTab = tabs.find((t) => t.id === activeId);
  const consistent =
    !!activeTab && activeTab.path === location.pathname && activeTab.search === location.search;
  if (activeTab && consistent) elements.current[activeTab.id] = outlet;

  const switchTo = (t: Tab) => {
    if (t.id === activeId) return;
    navigate(t.path + t.search, { state: { switchTo: t.id } });
  };

  const closeTab = (e: ReactMouseEvent, t: Tab) => {
    e.stopPropagation();
    if (tabs.length <= 1) return;
    const idx = tabs.findIndex((x) => x.id === t.id);
    const remaining = tabs.filter((x) => x.id !== t.id);
    delete elements.current[t.id];
    tabsRef.current = remaining;
    setTabs(remaining);
    if (t.id === activeId) {
      const neighbor = remaining[Math.min(idx, remaining.length - 1)];
      activeRef.current = neighbor.id;
      setActiveId(neighbor.id);
      navigate(neighbor.path + neighbor.search, { state: { switchTo: neighbor.id } });
    }
  };

  const closeOthers = () => {
    if (!activeTab) return;
    for (const t of tabs) if (t.id !== activeTab.id) delete elements.current[t.id];
    tabsRef.current = [activeTab];
    setTabs([activeTab]);
  };

  return (
    <div className="flex bg-white min-h-screen">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Header />

        {tabs.length > 1 && (
          <div className="flex items-stretch gap-1 overflow-x-auto border-b border-gray-200 bg-gray-50 px-2 pt-1" role="tablist" aria-label="الصفحات المفتوحة">
            {tabs.map((t) => {
              const active = t.id === activeId;
              return (
                <div
                  key={t.id}
                  role="tab"
                  aria-selected={active}
                  onClick={() => switchTo(t)}
                  className={`group flex max-w-[200px] shrink-0 cursor-pointer items-center gap-2 rounded-t-lg border border-b-0 px-3 py-1.5 text-sm ${
                    active
                      ? "border-gray-200 bg-white font-semibold text-orange-600"
                      : "border-transparent text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  <span className="truncate">{t.title}</span>
                  <button
                    type="button"
                    aria-label={`إغلاق ${t.title}`}
                    onClick={(e) => closeTab(e, t)}
                    className="rounded p-0.5 text-gray-400 hover:bg-gray-200 hover:text-gray-700"
                  >
                    <X className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                </div>
              );
            })}
            <button
              type="button"
              onClick={closeOthers}
              className="mr-auto shrink-0 self-center px-2 text-xs text-gray-500 hover:text-red-600"
            >
              إغلاق الباقي
            </button>
          </div>
        )}

        <main className="flex-1 overflow-y-auto">
          {tabs.length === 0
            ? outlet
            : tabs.map((t) => {
                const isActive = t.id === activeId;
                const el = isActive && consistent ? outlet : elements.current[t.id] ?? null;
                const value = isActive && consistent
                  ? { location, navigationType: navType }
                  : {
                      location: { pathname: t.path, search: t.search, hash: "", state: null, key: t.id },
                      navigationType: navType,
                    };
                return (
                  <div key={t.id} style={{ display: isActive ? "block" : "none" }}>
                    <LocationContext.Provider value={value}>{el}</LocationContext.Provider>
                  </div>
                );
              })}
        </main>
      </div>
    </div>
  );
}
